import { useEffect, useMemo, useState } from 'react';

import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import CompactSelectField from '../../components/Forms/Input/CompactSelectField';
import SearchInputField from '../../components/Forms/Input/SearchInputField';
import Checkbox from '../../components/Forms/Input/Checkbox';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { roleLabel } from '../../constants/roleLabels';
import { moduleLabel, actionLabel } from '../../constants/permissionModules';
import type { PermissionCatalogItem } from '../../store/slices/rolePermissions/rolePermissionsSlice';

const sortedUnique = (arr: string[]) => Array.from(new Set(arr)).sort();

/**
 * 🔐 Panel administrativo: permisos por rol (Etapa 4).
 *
 * Endpoint solo-SUPPORT. Muestra la matriz agrupada por módulo, con filtro por
 * módulo y búsqueda. El rol SUPPORT es de solo lectura (siempre tiene todo) y
 * los permisos "solo Soporte" no se pueden marcar para otros roles.
 */
const PermissionsTable = () => {
  const {
    roles,
    rolePermissions,
    catalog,
    lockedRoles,
    getRolePermissions,
    updateRolePermissions,
    toggleRolePermission,
    setRolePermissionsForRole,
  } = useRolePermissions();

  const [selectedRole, setSelectedRole] = useState<string>('');
  const [moduleFilter, setModuleFilter] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');
  const [baseline, setBaseline] = useState<string[]>([]);

  useEffect(() => {
    getRolePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecciona el primer rol no bloqueado cuando llegan los datos.
  useEffect(() => {
    if (!selectedRole && roles.length) {
      const first = roles.find((r) => !lockedRoles.includes(r)) ?? roles[0];
      setSelectedRole(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  // Snapshot de referencia cada vez que cambia el rol (para el estado "sin guardar").
  useEffect(() => {
    if (selectedRole) {
      setBaseline([...(rolePermissions[selectedRole] ?? [])].sort());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const isLocked = lockedRoles.includes(selectedRole);

  const current = useMemo(
    () => new Set(rolePermissions[selectedRole] ?? []),
    [rolePermissions, selectedRole],
  );

  const dirty = useMemo(() => {
    const now = sortedUnique(rolePermissions[selectedRole] ?? []);
    return now.length !== baseline.length || now.some((p, i) => p !== baseline[i]);
  }, [rolePermissions, selectedRole, baseline]);

  const modules = useMemo(
    () => sortedUnique(catalog.map((c) => c.module)),
    [catalog],
  );

  // catalog -> { module: item[] } aplicando filtros
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const out: Record<string, PermissionCatalogItem[]> = {};
    catalog.forEach((item) => {
      if (moduleFilter !== 'ALL' && item.module !== moduleFilter) return;
      if (
        term &&
        !item.name.toLowerCase().includes(term) &&
        !(item.label ?? '').toLowerCase().includes(term) &&
        !(item.description ?? '').toLowerCase().includes(term)
      )
        return;
      (out[item.module] ??= []).push(item);
    });
    Object.values(out).forEach((list) =>
      list.sort((a, b) => a.action.localeCompare(b.action)),
    );
    return out;
  }, [catalog, moduleFilter, search]);

  const groupModules = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const totalAssignable = catalog.filter((c) => !c.supportOnly).length;

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: r,
        label: lockedRoles.includes(r)
          ? `${roleLabel(r)} — solo lectura`
          : roleLabel(r),
      })),
    [roles, lockedRoles],
  );

  const moduleOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Todos los módulos' },
      ...modules.map((m) => ({ value: m, label: moduleLabel(m) })),
    ],
    [modules],
  );

  const toggleModule = (mod: string, check: boolean) => {
    if (isLocked) return;
    const modPerms = catalog
      .filter((c) => c.module === mod && !c.supportOnly)
      .map((c) => c.name);
    const base = new Set(rolePermissions[selectedRole] ?? []);
    if (check) modPerms.forEach((p) => base.add(p));
    else modPerms.forEach((p) => base.delete(p));
    setRolePermissionsForRole(selectedRole, Array.from(base));
  };

  const handleSave = async () => {
    const ok = await updateRolePermissions(selectedRole);
    if (ok) setBaseline(sortedUnique(rolePermissions[selectedRole] ?? []));
  };

  const handleReset = () => setRolePermissionsForRole(selectedRole, baseline);

  return (
    <DefaultLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <div className="space-y-6 p-6">
          {/* Encabezado */}
          <div>
            <h2 className="text-title-sm font-semibold text-black dark:text-white">
              Permisos por rol
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-body dark:text-bodydark">
              Asigná qué puede hacer cada rol. El rol{' '}
              <span className="font-semibold text-black dark:text-white">
                Soporte técnico
              </span>{' '}
              siempre tiene acceso completo y no se edita. Los permisos marcados{' '}
              <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                🔒 Solo Soporte
              </span>{' '}
              no pueden asignarse a otros roles.
            </p>
          </div>

          {/* Toolbar */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,16rem)_minmax(0,16rem)_1fr_auto] lg:items-end">
            <div>
              <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                Rol
              </label>
              <CompactSelectField
                name="role"
                value={selectedRole}
                options={roleOptions}
                onChange={(e) => setSelectedRole(String(e.target.value))}
                iconKey="user"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                Módulo
              </label>
              <CompactSelectField
                name="module"
                value={moduleFilter}
                options={moduleOptions}
                onChange={(e) => setModuleFilter(String(e.target.value))}
                iconKey="filter"
              />
            </div>

            <SearchInputField
              name="permissionSearch"
              label="Buscar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="nombre o descripción…"
              size="sm"
              withMargin={false}
            />

            <div className="rounded-lg border border-stroke bg-gray-2 px-4 py-2.5 text-center dark:border-strokedark dark:bg-meta-4 lg:mb-[1px]">
              <span className="block text-lg font-semibold text-black dark:text-white">
                {current.size}
                <span className="text-body dark:text-bodydark"> / {totalAssignable}</span>
              </span>
              <span className="text-xs text-body dark:text-bodydark">permisos</span>
            </div>
          </div>

          {isLocked && (
            <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-black dark:text-white">
              <span className="text-lg leading-none">🔒</span>
              <p>
                El rol{' '}
                <span className="font-semibold">{roleLabel(selectedRole)}</span>{' '}
                siempre tiene acceso completo. Esta vista es de solo lectura.
              </p>
            </div>
          )}
        </div>

        {/* Grid por módulo */}
        <div className="space-y-5 px-6 pb-6">
          {groupModules.length === 0 && (
            <p className="py-8 text-center text-sm text-body dark:text-bodydark">
              No hay permisos que coincidan con el filtro.
            </p>
          )}

          {groupModules.map((mod) => {
            const items = grouped[mod];
            const assignable = items.filter((i) => !i.supportOnly);
            const allChecked =
              assignable.length > 0 &&
              assignable.every((i) => current.has(i.name));
            const checkedInModule = items.filter((i) =>
              current.has(i.name),
            ).length;

            return (
              <div
                key={mod}
                className="overflow-hidden rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
              >
                <div className="flex items-center justify-between gap-4 border-b border-stroke bg-gray-2 px-4 py-3 dark:border-strokedark dark:bg-meta-4">
                  <h3 className="flex items-center gap-2 font-semibold text-black dark:text-white">
                    {moduleLabel(mod)}
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-normal text-body dark:bg-boxdark dark:text-bodydark">
                      {checkedInModule}/{items.length}
                    </span>
                  </h3>
                  {!isLocked && assignable.length > 0 && (
                    <Checkbox
                      label="Marcar todo el módulo"
                      checked={allChecked}
                      onChange={() => toggleModule(mod, !allChecked)}
                      className="text-xs"
                    />
                  )}
                </div>

                <ul className="divide-y divide-stroke dark:divide-strokedark">
                  {items.map((item) => {
                    const checked = current.has(item.name);
                    const disabled = isLocked || item.supportOnly;
                    return (
                      <li
                        key={item.name}
                        className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-1 dark:hover:bg-meta-4/40"
                      >
                        <div className="pt-0.5">
                          <Checkbox
                            id={`perm-${item.name}`}
                            checked={checked}
                            disabled={disabled}
                            onChange={() =>
                              toggleRolePermission(selectedRole, item.name)
                            }
                          />
                        </div>

                        <label
                          htmlFor={`perm-${item.name}`}
                          className={`min-w-0 flex-1 ${
                            disabled ? '' : 'cursor-pointer'
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-black dark:text-white">
                              {item.label || item.name}
                            </span>
                            <span className="rounded-full bg-gray-2 px-2 py-0.5 text-[11px] text-body dark:bg-meta-4 dark:text-bodydark">
                              {actionLabel(item.action)}
                            </span>
                            {item.supportOnly && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
                                🔒 Solo Soporte
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="mt-0.5 text-sm text-body dark:text-bodydark">
                              {item.description}
                            </p>
                          )}
                          <p className="mt-0.5 font-mono text-[11px] text-body/70 dark:text-bodydark/70">
                            {item.name}
                          </p>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-stroke bg-white px-6 py-4 dark:border-strokedark dark:bg-boxdark">
          {dirty && !isLocked && (
            <span className="mr-auto inline-flex items-center gap-2 text-sm font-medium text-warning">
              <span className="h-2 w-2 rounded-full bg-warning" />
              Cambios sin guardar
            </span>
          )}
          <button
            type="button"
            onClick={handleReset}
            disabled={!dirty || isLocked}
            className="rounded-lg border border-stroke px-4 py-2 text-sm font-medium text-black transition hover:bg-gray-1 disabled:opacity-40 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || isLocked}
            className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:opacity-40"
          >
            Guardar {selectedRole ? roleLabel(selectedRole) : ''}
          </button>
        </div>
      </SectionWrapper>
    </DefaultLayout>
  );
};

export default PermissionsTable;
